import AddIcon from '@mui/icons-material/Add';
import { Box, Grid, styled, Switch } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { FormProvider, Controller, useForm } from 'react-hook-form';
import { WsTypography, WsTypoVariants, WsButton, Label } from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard';
import { SettingsPageLayout } from '../../../components/SettingsPageLayout';
import { useWorkspaceContext } from '../../../context';
import { TitleAndBreadCrumbs } from '../../../features/invoicing/helper/InvocieSettingsHelper';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../../hooks/useAtsCustomAPIAccess';
import { clientSettingsIndexOptions } from '../helper/ClientSettingsHelper';

export interface ILabelWithStatus {
  label: string;
  labelTwo?: string;
  status: boolean;
}

export const HeaderBox = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'row',
  minWidth: '100%',
}));

export const StyledSwitch = styled(Switch)(() => ({
  top: '0.5vw',
}));

interface IClientSettingsLayout {
  breadCrumbAndTitle: TitleAndBreadCrumbs;
  headerList: string[];
  itemsList: ILabelWithStatus[];
  dataCallback(data: boolean, index: number): void;
  handleAddButton?: () => void;
  addButtonText?: string;
  backUrl: string;
}

const ClientSettingsLayout = ({
  breadCrumbAndTitle,
  headerList,
  dataCallback,
  itemsList,
  addButtonText,
  handleAddButton,
  backUrl,
}: IClientSettingsLayout) => {
  const methods = useForm();
  const { control } = methods;
  const itemXS = 12 / headerList.length;

  const workspaceContext = useWorkspaceContext();
  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_CLIENT_SETTINGS,
  ]);

  const manageAccess = workspaceContext.owns || atsCustomAccessControl[0];

  return (
    <SettingsPageLayout
      title={breadCrumbAndTitle.Title}
      backUrl={backUrl}
      indexOptions={clientSettingsIndexOptions}
      breadCrumnbsList={breadCrumbAndTitle.BreadcrumbsList}
    >
      <Box component="form" px={8} pt={2}>
        <FormProvider {...methods}>
          <HeaderBox>
            {headerList?.map((header, index) => (
              <Grid item xs={itemXS} key={index}>
                <WsTypography variant={WsTypoVariants.SUB_TITLE}>{header}</WsTypography>
              </Grid>
            ))}
          </HeaderBox>
          {isEmpty(itemsList) ? (
            <NoDataFoundCard
              text="No Data found, Please click below button to add settings"
              subElement={
                <Box p={2}>
                  <WsButton
                    startIcon={<AddIcon />}
                    variant="outlined"
                    onClick={handleAddButton}
                  >{`Add ${addButtonText}`}</WsButton>
                </Box>
              }
            />
          ) : (
            <Grid container pt={6} spacing={2}>
              {itemsList?.map((item, index) => (
                <>
                  <Grid item xs={itemXS}>
                    <Label sx={{ fontWeight: 'bold' }}>{item.label}</Label>
                  </Grid>
                  {item.labelTwo && (
                    <Grid item xs={itemXS}>
                      <Label sx={{ fontWeight: 'bold' }}>{item.labelTwo}</Label>
                    </Grid>
                  )}
                  <Grid item xs={itemXS}>
                    <Controller
                      name="value"
                      control={control}
                      render={(props) => (
                        <StyledSwitch
                          disabled={!manageAccess}
                          defaultChecked={props.field.value ?? item.status}
                          onChange={(_event, checked) => dataCallback(checked, index)}
                        />
                      )}
                    />
                  </Grid>
                </>
              ))}
            </Grid>
          )}
          <Grid pt={6}>
            {addButtonText && !isEmpty(itemsList) && (
              <WsButton
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={handleAddButton}
              >{`Add ${addButtonText}`}</WsButton>
            )}
          </Grid>
        </FormProvider>
      </Box>
    </SettingsPageLayout>
  );
};

export default ClientSettingsLayout;
